import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import Layout from '../../components/common/pageLayout';
import LeftSidebar from '../../components/MailBox/leftSidebar';
import RightSidebar from '../../components/MailBox/rightSidebar';
import { v4_mailbox_configuration, v4_mailbox_configure, v4_mailbox_email, v4_mailbox_emails, v4_mailbox_folders, v4_mailbox_handleMailAction } from '../../actions/v4/mailBox';
import Configure from '../../components/MailBox/configure';
import { toast } from 'react-toastify';
import { getSessionStorage, getStorage, isVesselCrew, removeStorage, setSessionStorage, setStorage } from '../../utils';
import constant from '../../constant';
import ChatWindow from '../../components/MailBox/chatWindow';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { useMessageContext } from '../../context/MessageContext';

const getSearchQueryFromLocation = (locationSearch = '') => {
    const params = new URLSearchParams(locationSearch || '');
    return (params.get('search') || '').trim();
};

const coercePositiveInteger = (...values) => {
    for (const value of values) {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return null;
};

const getOpenMailTargetFromLocation = (location = {}) => {
    const params = new URLSearchParams(location?.search || '');
    const locationState = location?.state || {};

    const messageId = String(
        params.get('openMessageId')
        || locationState.openMessageId
        || ''
    ).trim();

    if (!messageId) {
        return null;
    }

    const mailboxId = String(
        params.get('openMailboxId')
        || locationState.openMailboxId
        || ''
    ).trim();

    const folder = String(
        params.get('openFolder')
        || locationState.openFolder
        || 'INBOX'
    ).trim() || 'INBOX';

    return {
        messageId,
        mailboxId,
        folder
    };
};

const getOpenChatTargetFromLocation = (location = {}) => {
    const params = new URLSearchParams(location?.search || '');
    const locationState = location?.state || {};

    const userId = coercePositiveInteger(
        params.get('openChatUserId'),
        locationState.openChatUserId,
        locationState.chatUserId
    );

    if (!userId) {
        return null;
    }

    const fullName = String(
        params.get('openChatUserName')
        || locationState.openChatUserName
        || locationState.chatUserName
        || ''
    ).trim();

    const profileImageUrl = String(
        params.get('openChatUserProfile')
        || locationState.openChatUserProfile
        || ''
    ).trim();

    return {
        userId,
        fullName,
        profileImageUrl
    };
};

const formatFolderLabel = (value = '') => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
        return '';
    }

    const segmentValue = normalizedValue.split('/').filter(Boolean).pop() || normalizedValue;
    return segmentValue
        .split(/[\s._-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const Index = (props) => {
    const canUseChat = !isVesselCrew();
    const initialSearchQuery = getSearchQueryFromLocation(props.location?.search);
    const initialOpenMailTarget = getOpenMailTargetFromLocation(props.location);
    const initialOpenChatTarget = canUseChat ? getOpenChatTargetFromLocation(props.location) : null;

    const [host, setHost] = useState(null);
    const [selectedMailboxId, setSelectedMailboxId] = useState('');
    const [loginStatus, setLoginStatus] = useState('pending'); // 'pending' | 'logged_in' | 'not_logged_in'
    const [folders, setFolders] = useState([]);
    const [emails, setEmails] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [showLoader, setShowLoader] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState('INBOX');
    const [searchInput, setSearchInput] = useState(initialSearchQuery);
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
    const [readmail, setReadmail] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState([]);
    const [mode, setMode] = useState('mail');
    const [chatUser, setChatUser] = useState({});
    const [mailBoxes, setMailBox] = useState(null);
    const [pendingOpenMailTarget, setPendingOpenMailTarget] = useState(initialOpenMailTarget);
    const [pendingOpenChatTarget, setPendingOpenChatTarget] = useState(initialOpenChatTarget);
    const lastUrlSearchRef = useRef(initialSearchQuery);
    const resolvingOpenMailRef = useRef(false);
    const preserveReadMailOnNextFetchRef = useRef(false);
    const { setActiveChatUser } = useMessageContext();


    const onMicrosoftSSO = () => {
        const azureClientId = process.env.REACT_APP_AZURE_CLIENT_ID;

        if (!azureClientId) {
            toast.error("Microsoft SSO is not configured. Missing Azure client ID.");
            return;
        }

        const baseUrl = window.location.origin;
        const redirectUri = `${baseUrl}/auth/callback`;

        const authUrl =
            `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize` +
            `?client_id=${azureClientId}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&response_mode=query` +
            `&scope=${encodeURIComponent(
                "openid profile email offline_access Mail.ReadWrite Mail.Send"
            )}`;

        window.location.href = authUrl;
    };

    useEffect(() => {
        v4_mailbox_configuration().then((res) => {
            if (res.status) {
                setMailBox(res.result);
                if (res.result?.length > 0) {
                    setLoginStatus('logged_in');
                }
                else {
                    setLoginStatus('not_logged_in');
                }
            } else {
                setLoginStatus('not_logged_in');
                toast.error(res.error || "Failed to load mailbox configuration");
            }
        }).catch((err) => {
            console.error(err);
            setLoginStatus('not_logged_in');
            toast.error("Failed to load mailbox configuration");
        });
    }, []);
    useEffect(() => {
        if (getStorage(constant.keys.mailFolder)) {
            setLoginStatus('logged_in');
            setFolders(JSON.parse(atob(getStorage(constant.keys.mailFolder))));
        }
        else {
            if (selectedMailboxId) {
                fetchFolders()
            }
        }

    }, [])
    useEffect(() => {
        if (selectedMailboxId) {
            fetchFolders()
        }


    }, [selectedMailboxId])
    const fetchFolders = () => {
        v4_mailbox_folders(selectedMailboxId).then((res) => {
            if (res.status) {
                setStorage(constant.keys.mailFolder, btoa(JSON.stringify(res.result.folders || [])));
                setFolders(res.result.folders || []);
                setHost(res.result.host)
            } else {
                // if (res.error === "Not Logged in") {
                //     setLoginStatus('not_logged_in');
                // } else {
                toast.error(res.error || "Failed to load folders");
                // }
            }
        });
    };

    const fetchEmails = () => {
        setShowLoader(true);
        if (selectedMailboxId) {
            v4_mailbox_emails(page, limit, selectedFolder, selectedMailboxId, searchQuery).then((res) => {
                console.log(res);
                
                if (res.status) {
                    setEmails(res.result);
                    if (preserveReadMailOnNextFetchRef.current) {
                        preserveReadMailOnNextFetchRef.current = false;
                    } else {
                        setReadmail(false);
                    }
                    setSelectedMessageIds([]);
                } else {
                    if (res.error === "Not Logged in") {
                        setLoginStatus('not_logged_in');
                    } else {
                        toast.error(res.error || "Failed to fetch emails");
                    }
                }
            }).finally(() => {
                setShowLoader(false);
            });
        }
        else {
            setShowLoader(false);

        }

    };
    const updateSearchQueryParam = useCallback((queryValue) => {
        const normalizedSearch = (queryValue || '').trim();
        const params = new URLSearchParams(props.location?.search || '');

        if (normalizedSearch) {
            params.set('search', normalizedSearch);
        } else {
            params.delete('search');
        }

        const nextSearch = params.toString() ? `?${params.toString()}` : '';
        const currentSearch = props.location?.search || '';

        if (nextSearch !== currentSearch) {
            props.history.replace({
                pathname: props.location?.pathname,
                search: nextSearch
            });
        }
    }, [props.history, props.location]);

    const handleSearchSubmit = useCallback((queryText) => {
        const normalizedSearch = (queryText || '').trim();
        setSearchInput(normalizedSearch);
        setPage(1);
        setReadmail(false);
        setSearchQuery((prev) => (prev === normalizedSearch ? prev : normalizedSearch));
        updateSearchQueryParam(normalizedSearch);
    }, [updateSearchQueryParam]);

    const handleSearchClear = useCallback(() => {
        setSearchInput('');
        setPage(1);
        setReadmail(false);
        setSearchQuery((prev) => (prev === '' ? prev : ''));
        updateSearchQueryParam('');
    }, [updateSearchQueryParam]);

    const clearOpenMailQueryParams = useCallback(() => {
        const params = new URLSearchParams(props.location?.search || '');
        let hasChanged = false;

        ['openMessageId', 'openMailboxId', 'openFolder'].forEach((key) => {
            if (params.has(key)) {
                params.delete(key);
                hasChanged = true;
            }
        });

        if (hasChanged) {
            const nextSearch = params.toString() ? `?${params.toString()}` : '';
            props.history.replace({
                pathname: props.location?.pathname,
                search: nextSearch
            });
        }
    }, [props.history, props.location?.pathname, props.location?.search]);

    const clearOpenChatQueryParams = useCallback(() => {
        const params = new URLSearchParams(props.location?.search || '');
        let hasChanged = false;

        ['openChatUserId', 'openChatUserName', 'openChatUserProfile'].forEach((key) => {
            if (params.has(key)) {
                params.delete(key);
                hasChanged = true;
            }
        });

        if (hasChanged) {
            const nextSearch = params.toString() ? `?${params.toString()}` : '';
            props.history.replace({
                pathname: props.location?.pathname,
                search: nextSearch
            });
        }
    }, [props.history, props.location?.pathname, props.location?.search]);

    useEffect(() => {
        const queryFromUrl = getSearchQueryFromLocation(props.location?.search);
        if (lastUrlSearchRef.current === queryFromUrl) {
            return;
        }

        lastUrlSearchRef.current = queryFromUrl;
        setSearchInput(queryFromUrl);
        setSearchQuery(queryFromUrl);
        setPage(1);
        setReadmail(false);
    }, [props.location?.search]);

    useEffect(() => {
        const routeTarget = getOpenMailTargetFromLocation(props.location);
        setPendingOpenMailTarget((prev) => {
            const prevKey = prev ? `${prev.messageId}|${prev.mailboxId}|${prev.folder}` : '';
            const nextKey = routeTarget ? `${routeTarget.messageId}|${routeTarget.mailboxId}|${routeTarget.folder}` : '';
            return prevKey === nextKey ? prev : routeTarget;
        });
    }, [props.location]);

    useEffect(() => {
        if (!canUseChat) {
            setPendingOpenChatTarget(null);
            clearOpenChatQueryParams();
            return;
        }

        const routeTarget = getOpenChatTargetFromLocation(props.location);
        setPendingOpenChatTarget((prev) => {
            const prevKey = prev ? `${prev.userId}|${prev.fullName}|${prev.profileImageUrl}` : '';
            const nextKey = routeTarget ? `${routeTarget.userId}|${routeTarget.fullName}|${routeTarget.profileImageUrl}` : '';
            return prevKey === nextKey ? prev : routeTarget;
        });
    }, [canUseChat, clearOpenChatQueryParams, props.location]);

    useEffect(() => {
        const savedMailboxId = getSessionStorage(constant.keys.mailBox);
        const savedMailboxIsAccessible = mailBoxes?.some(
            (mailbox) => String(mailbox?.ID) === String(savedMailboxId)
        );

        // The saved selection can belong to the previous user in the same
        // browser session. A vessel login must fall back to its assigned shared
        // mailbox instead of remaining pointed at an inaccessible mailbox ID.
        const mailboxId = savedMailboxIsAccessible
            ? savedMailboxId
            : (mailBoxes?.[0]?.ID || '');
        setSelectedMailboxId(mailboxId);
        setSessionStorage(constant.keys.mailBox, mailboxId);
    }, [mailBoxes])
    useEffect(() => {
        if (loginStatus === 'logged_in') {
            fetchEmails();
        }
    }, [page, limit, selectedFolder, loginStatus, refresh, selectedMailboxId, searchQuery]);

    useEffect(() => {
        if (!pendingOpenMailTarget?.messageId || loginStatus !== 'logged_in') {
            return;
        }
        if (resolvingOpenMailRef.current) {
            return;
        }
        if (!Array.isArray(mailBoxes) || mailBoxes.length === 0) {
            return;
        }

        const targetMessageId = String(pendingOpenMailTarget.messageId);
        const listedMail = Array.isArray(emails?.mails)
            ? emails.mails.find((mail) => String(mail?.messageId || '') === targetMessageId)
            : null;

        if (listedMail) {
            const mailToOpen = listedMail.read === false ? { ...listedMail, read: true } : listedMail;
            setReadmail(mailToOpen);

            if (listedMail.read === false && selectedMailboxId) {
                v4_mailbox_handleMailAction({
                    folder: selectedFolder,
                    messageId: targetMessageId,
                    action: 'read',
                    selectedMailboxId,
                }).catch(() => null);
            }

            setPendingOpenMailTarget(null);
            clearOpenMailQueryParams();
            return;
        }

        let isCancelled = false;
        resolvingOpenMailRef.current = true;

        const openFromMailboxApi = async () => {
            const pushUnique = (list, value) => {
                const normalized = String(value || '').trim();
                if (!normalized || list.includes(normalized)) {
                    return;
                }
                list.push(normalized);
            };

            const mailboxCandidates = [];
            const folderCandidates = [];

            pushUnique(mailboxCandidates, pendingOpenMailTarget.mailboxId);
            pushUnique(mailboxCandidates, selectedMailboxId);
            (mailBoxes || []).forEach((mailbox) => pushUnique(mailboxCandidates, mailbox?.ID));

            pushUnique(folderCandidates, pendingOpenMailTarget.folder);
            pushUnique(folderCandidates, selectedFolder);
            pushUnique(folderCandidates, 'INBOX');

            for (const mailboxIdCandidate of mailboxCandidates) {
                for (const folderCandidate of folderCandidates) {
                    try {
                        const response = await v4_mailbox_email(targetMessageId, folderCandidate, mailboxIdCandidate);
                        const resolvedMail = response?.result?.mail;

                        if (!response?.status || !resolvedMail?.messageId) {
                            continue;
                        }

                        if (isCancelled) {
                            return;
                        }

                        preserveReadMailOnNextFetchRef.current = true;
                        setSelectedMailboxId(mailboxIdCandidate);
                        setSelectedFolder(folderCandidate);

                        const mailToOpen = resolvedMail.read === false
                            ? { ...resolvedMail, read: true }
                            : resolvedMail;
                        setReadmail(mailToOpen);

                        if (resolvedMail.read === false) {
                            v4_mailbox_handleMailAction({
                                folder: folderCandidate,
                                messageId: targetMessageId,
                                action: 'read',
                                selectedMailboxId: mailboxIdCandidate,
                            }).catch(() => null);
                        }

                        setPendingOpenMailTarget(null);
                        clearOpenMailQueryParams();
                        return;
                    } catch (error) {
                        // Keep trying mailbox/folder combinations until one succeeds.
                    }
                }
            }

            if (!isCancelled) {
                toast.warn('Could not open this email directly. Showing mailbox list instead.');
                setPendingOpenMailTarget(null);
                clearOpenMailQueryParams();
            }
        };

        openFromMailboxApi().finally(() => {
            resolvingOpenMailRef.current = false;
        });

        return () => {
            isCancelled = true;
        };
    }, [
        clearOpenMailQueryParams,
        emails?.mails,
        loginStatus,
        mailBoxes,
        pendingOpenMailTarget,
        selectedFolder,
        selectedMailboxId
    ]);

    useEffect(() => {
        if (!canUseChat) {
            setMode('mail');
            setPendingOpenChatTarget(null);
            clearOpenChatQueryParams();
            return;
        }

        if (!pendingOpenChatTarget?.userId) {
            return;
        }

        const targetUserId = Number(pendingOpenChatTarget.userId);
        if (!Number.isInteger(targetUserId) || targetUserId <= 0) {
            setPendingOpenChatTarget(null);
            clearOpenChatQueryParams();
            return;
        }

        const normalizedFullName = String(pendingOpenChatTarget.fullName || '').trim();
        const normalizedProfileImageUrl = String(pendingOpenChatTarget.profileImageUrl || '').trim();

        setMode('chat');
        setChatUser((prev) => {
            const previousChatUserId = Number(prev?.USER_ID);
            const fallbackName = `User ${targetUserId}`;

            if (Number.isInteger(previousChatUserId) && previousChatUserId === targetUserId) {
                return {
                    ...prev,
                    USER_ID: targetUserId,
                    FULL_NAME: normalizedFullName || prev?.FULL_NAME || fallbackName,
                    PROFILE_IMAGE_URL: normalizedProfileImageUrl || prev?.PROFILE_IMAGE_URL || '',
                };
            }

            return {
                USER_ID: targetUserId,
                FULL_NAME: normalizedFullName || fallbackName,
                ROLE_NAME: '',
                PROFILE_IMAGE_URL: normalizedProfileImageUrl,
            };
        });
        setActiveChatUser(targetUserId);
        setPendingOpenChatTarget(null);
        clearOpenChatQueryParams();
    }, [canUseChat, clearOpenChatQueryParams, pendingOpenChatTarget, setActiveChatUser]);

    useEffect(() => {
        if (!canUseChat) {
            setActiveChatUser(null);
            return;
        }

        if (mode === 'chat' && chatUser?.USER_ID) {
            setActiveChatUser(chatUser.USER_ID);
            return;
        }
        setActiveChatUser(null);
    }, [canUseChat, mode, chatUser?.USER_ID, setActiveChatUser]);

    useEffect(() => () => {
        setActiveChatUser(null);
    }, [setActiveChatUser]);

    const onConfigure = (payload) => {
        v4_mailbox_configure(payload).then((res) => {
            if (res.status) {
                removeStorage(constant.keys.mailFolder)
                toast.success(res.result.message || "Mailbox configured successfully");
                fetchFolders();
            } else {
                toast.error(res.error || "Configuration failed");
            }
        });
    };

    const handleFolderChange = useCallback((folder) => {
        setPage(1);
        setReadmail(false);
        setSelectedFolder(folder);

    }, []);

    const selectedFolderLabel = useMemo(() => {
        const normalizedSelectedFolder = String(selectedFolder || '').trim();
        if (!normalizedSelectedFolder) {
            return 'Inbox';
        }

        if (host === 'microsoft') {
            const microsoftFolder = (folders || []).find(
                (folder) => String(folder?.id || '').trim() === normalizedSelectedFolder
            );
            if (microsoftFolder?.displayName) {
                return microsoftFolder.displayName;
            }
        }

        const genericFolder = (folders || []).find((folder) => {
            const folderPath = String(folder?.path || '').trim();
            const folderLabel = String(folder?.label || '').trim();
            return folderPath === normalizedSelectedFolder || folderLabel === normalizedSelectedFolder;
        });

        if (genericFolder?.label) {
            return formatFolderLabel(genericFolder.label);
        }

        return formatFolderLabel(normalizedSelectedFolder) || 'Inbox';
    }, [folders, host, selectedFolder]);

    if (loginStatus === 'pending') {
        return (
            <Layout title="Mailbox">
                <div className="text-center p-5">
                    <span>Loading mailbox...</span>
                </div>
            </Layout>
        );
    }

    if (loginStatus === 'not_logged_in') {
        return (
            <Layout title="Configure Mailbox">
                <Configure onSubmit={onConfigure}
                    onMicrosoftSSO={onMicrosoftSSO}
                />
            </Layout>
        );
    }

    const normalizeMessageIds = (messageId) => {
        if (Array.isArray(messageId)) {
            return messageId.filter(Boolean);
        }
        return messageId ? [messageId] : [];
    };

    const updateLocalEmailsByAction = (messageIds, action) => {
        if (!['read', 'unread', 'reply', 'replyAll', 'forward'].includes(action)) {
            return;
        }

        const patchMailByAction = (mail) => {
            if (!messageIds.includes(mail.messageId)) {
                return mail;
            }

            if (action === 'read' || action === 'unread') {
                return { ...mail, read: action === 'read' };
            }

            if (action === 'forward') {
                return { ...mail, isForwarded: true, read: true };
            }

            return { ...mail, isReplied: true, read: true };
        };

        setEmails((prev) => {
            if (!prev || !Array.isArray(prev.mails)) {
                return prev;
            }

            return {
                ...prev,
                mails: prev.mails.map((mail) => patchMailByAction(mail))
            };
        });

        setReadmail((prev) => {
            if (!prev || typeof prev !== 'object' || !messageIds.includes(prev.messageId)) {
                return prev;
            }

            return patchMailByAction(prev);
        });

        setSelectedMessageIds((prev) => prev.filter((id) => !messageIds.includes(id)));
    };

    const handleMailAction = (messageId, action, content, extraPayload = {}) => {
        const messageIds = normalizeMessageIds(messageId);
        if (!messageIds.length) {
            toast.error('Please select mail');
            return Promise.resolve();
        }
        if (action === 'delete') {
            const error = 'Delete email is disabled.';
            toast.error(error);
            return Promise.resolve({ status: false, error });
        }

        const requestMessageId = Array.isArray(messageId) ? messageIds : messageIds[0];
        return v4_mailbox_handleMailAction({
            folder: selectedFolder,
            messageId: requestMessageId,
            action,
            content,
            selectedMailboxId,
            ...extraPayload
        }).then((res) => {
            if (res.status) {
                fetchFolders();
                updateLocalEmailsByAction(messageIds, action);
            } else {
                toast.error(res.error || "Failed to perform mail action");
            }

            return res;
        }).catch(() => {
            toast.error("Failed to perform mail action");
        });
    }

    const selectedMailboxEmail = (mailBoxes || []).find(
        (item) => String(item?.ID) === String(selectedMailboxId)
    )?.EMAIL || '';

    return (
        <Layout title={`Mail Box / ${selectedFolderLabel}`} nonCardVersion={true}>
            <div className="row">
                <div className="col-md-12">
                    <LeftSidebar
                        host={host}
                        selectedMailboxId={selectedMailboxId}
                        setSelectedMailboxId={setSelectedMailboxId}
                        setChatUser={setChatUser}
                        mailBoxes={mailBoxes}
                        setMode={setMode}
                        folders={folders}
                        selectedFolder={selectedFolder}
                        setSelectedFolder={handleFolderChange}
                        onlineUsers={props?.onlineUsers}
                        canUseChat={canUseChat}

                    />
                    {mode === 'mail' && <RightSidebar
                        host={host}
                        onRefresh={() => setRefresh((prev) => !prev)}
                        selectedMessageIds={selectedMessageIds}
                        setSelectedMessageIds={setSelectedMessageIds}
                        handleMailAction={handleMailAction}
                        folderName={selectedFolder}
                        folderDisplayName={selectedFolderLabel}
                        data={emails}
                        setPage={setPage}
                        showLoader={showLoader}
                        readmail={readmail}
                        setReadmail={setReadmail}
                        selectedMailboxId={selectedMailboxId}
                        selectedMailboxEmail={selectedMailboxEmail}
                        searchInput={searchInput}
                        setSearchInput={setSearchInput}
                        searchQuery={searchQuery}
                        onSearchSubmit={handleSearchSubmit}
                        onSearchClear={handleSearchClear}
                    />}
                    {canUseChat && mode === 'chat' && <ChatWindow
                        user={chatUser}
                        onlineUsers={props?.onlineUsers}
                    />}
                </div>
            </div>
        </Layout>
    );
};

export default withRouter(Index);
